import React, { useCallback, useState } from 'react';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import type { GridPaginationModel } from '@mui/x-data-grid';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  type SamplingSchedulesListFilters,
  type StrapiSamplingScheduleRow,
  strapiListSamplingSchedules,
} from '../../../../api/strapiCrm';
import { DATA_GRID_DEFAULT_PAGE_SIZE } from '../../../../components/DataGridNumberedPagination';
import { SamplingScheduleDataGrid } from '../../../../components/SamplingScheduleDataGrid';
import { useAuth } from '../../../../auth/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { LIST_FILTERS_APPLY_HINT, commitListFiltersOnEnter } from '../../../../ui/listFiltersApi';
import { CollapsibleListFilters } from '../../../../ui/CollapsibleListFilters';
import { SamplingSchedulesCalendarOverview } from '../CalendarOverview';

const STATUS_ALL = 'ALL' as const;
const STATUS_OPTIONS = [
  STATUS_ALL,
  'draft',
  'scheduling_in_progress',
  'rejected',
  'approved',
  'work_order_created',
  'cancelled',
] as const;

const STATUS_LABEL: Record<string, string> = {
  [STATUS_ALL]: '(Semua status)',
  draft: 'Draft',
  scheduling_in_progress: 'Penjadwalan',
  rejected: 'Ditolak',
  approved: 'Disetujui',
  work_order_created: 'WO dibuat',
  cancelled: 'Dibatalkan',
};

type FilterDraft = {
  quotationRefs: string;
  customer: string;
  status: string;
};

const initialDraft: FilterDraft = {
  quotationRefs: '',
  customer: '',
  status: STATUS_ALL,
};

function draftToApplied(d: FilterDraft): SamplingSchedulesListFilters {
  return {
    quotationRefs: d.quotationRefs.trim(),
    customer: d.customer.trim(),
    status: d.status === STATUS_ALL ? undefined : d.status,
  };
}

function initialFilterDraft(quotationFromUrl: string): FilterDraft {
  return {
    ...initialDraft,
    quotationRefs: quotationFromUrl,
  };
}

export function SamplingSchedulesListTabScreen() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const quotationFromUrl = searchParams.get('quotation')?.trim() ?? '';
  const { jwt } = useAuth();
  const [draft, setDraft] = useState<FilterDraft>(() => initialFilterDraft(quotationFromUrl));
  const [applied, setApplied] = useState<SamplingSchedulesListFilters>(() =>
    draftToApplied(initialFilterDraft(quotationFromUrl)),
  );
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: DATA_GRID_DEFAULT_PAGE_SIZE,
  });
  const [listView, setListView] = useState<'list' | 'calendar'>('calendar');

  const listQ = useQuery({
    queryKey: ['strapi', 'samplingSchedules', jwt, applied, paginationModel.page, paginationModel.pageSize],
    queryFn: () =>
      strapiListSamplingSchedules(jwt!, applied, {
        page: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
      }),
    enabled: Boolean(jwt),
  });

  const rows = listQ.data?.rows ?? [];
  const total = listQ.data?.total ?? 0;

  const commitFilters = useCallback(() => {
    setApplied(draftToApplied(draft));
    setPaginationModel((m) => ({ ...m, page: 0 }));
  }, [draft]);

  const goDetail = useCallback(
    (row: StrapiSamplingScheduleRow) => {
      navigate(`/sampling/schedules/${encodeURIComponent(row.documentId)}`);
    },
    [navigate],
  );

  const statusFilterChange = useCallback((status: string) => {
    setDraft((d) => {
      const next = { ...d, status };
      setApplied(draftToApplied(next));
      return next;
    });
    setPaginationModel((m) => ({ ...m, page: 0 }));
  }, []);

  return (
    <Box sx={{ width: '100%', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Lihat daftar dan kalender penjadwalan. Di kalender, hijau = terjadwal, abu-abu = semua titik
        berita acara sudah terbit. Buat work order dari modal detail penjadwalan.
      </Typography>

      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={listView}
          onChange={(_, value: 'list' | 'calendar' | null) => {
            if (value) setListView(value);
          }}
          aria-label="Tampilan penjadwalan"
        >
          <ToggleButton value="list" data-testid="sampling-schedules-view-list">
            Daftar
          </ToggleButton>
          <ToggleButton value="calendar" data-testid="sampling-schedules-view-calendar">
            Kalender
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {listView === 'calendar' ? (
        <SamplingSchedulesCalendarOverview jwt={jwt} />
      ) : (
        <>
      <CollapsibleListFilters
        filterToggleTestId="sampling-schedules-filter-toggle"
        actions={
          <Button
            type="button"
            variant="outlined"
            color="inherit"
            startIcon={
              listQ.isFetching ? (
                <CircularProgress size={16} thickness={5} aria-label="Memuat ulang…" />
              ) : (
                <RefreshIcon aria-hidden />
              )
            }
            disabled={!jwt || listQ.isFetching}
            onClick={() => void listQ.refetch()}
            data-testid="sampling-schedules-refresh-button"
          >
            Muat ulang
          </Button>
        }
      >
        <Typography variant="caption" color="text.secondary" display="block">
          {LIST_FILTERS_APPLY_HINT}
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gap: 1.5,
            gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
            alignItems: 'flex-start',
          }}
        >
          <TextField
            label="Quotation"
            value={draft.quotationRefs}
            onChange={(e) => setDraft((d) => ({ ...d, quotationRefs: e.target.value }))}
            onBlur={commitFilters}
            onKeyDown={commitListFiltersOnEnter(commitFilters)}
            size="small"
            fullWidth
          />
          <TextField
            label="Pelanggan"
            value={draft.customer}
            onChange={(e) => setDraft((d) => ({ ...d, customer: e.target.value }))}
            onBlur={commitFilters}
            onKeyDown={commitListFiltersOnEnter(commitFilters)}
            size="small"
            fullWidth
          />
          <FormControl size="small" fullWidth>
            <InputLabel id="sampling-schedules-filter-status-label">Status</InputLabel>
            <Select
              labelId="sampling-schedules-filter-status-label"
              label="Status"
              data-testid="sampling-schedules-filter-status"
              value={draft.status}
              onChange={(e) => statusFilterChange(String(e.target.value))}
            >
              {STATUS_OPTIONS.map((s) => (
                <MenuItem key={s} value={s}>{STATUS_LABEL[s] ?? s}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </CollapsibleListFilters>

      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
        Halaman ini: {rows.length} baris · Total terfilter: {total}
      </Typography>

      <SamplingScheduleDataGrid
        rows={rows}
        rowCount={total}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        isLoading={listQ.isPending || listQ.isFetching}
        isError={listQ.isError}
        error={listQ.error instanceof Error ? listQ.error : undefined}
        onView={goDetail}
      />
        </>
      )}
    </Box>
  );
}
